import { useMemo, useState, type FormEvent, type Ref } from 'react';
import { DataTable, GS_COMPONENTS, type Column } from '@gs/ui';
import { useTranslation } from '../../../i18n';
import type { AdminMemberTeam } from '../../../hooks/useAdminMembers';
import {
  useUpsertPayrollTargets,
  type AdminYearConfig,
} from '../../../hooks/useAdminRemunerationConfig';
import {
  isTeamTargetsOver100,
  remainingTeamTargetPoints,
  roundPoints,
  sumTeamTargetPoints,
} from '../../../lib/admin/payroll-targets';
import { SectionHeader } from '../SectionHeader';
import { FieldLabel } from './fields';
import { INPUT_MEDIUM, INPUT_SHORT, toFraction, toPoints } from './scale';

interface TargetsTableRow {
  year: number;
  revenuePct: number | null;
  teamCount: number;
  sum: number;
}

export function PayrollTargetsSection({
  year,
  yearConfigs,
  teamTargets,
  teams,
  autoFocus,
  inputRef,
  onError,
  onClear,
  onSuccess,
  onEditYear,
}: {
  year: string;
  yearConfigs: AdminYearConfig[];
  teamTargets: { year: number; teamId: string; targetPct: number }[];
  teams: AdminMemberTeam[];
  autoFocus: boolean;
  inputRef: Ref<HTMLInputElement>;
  onError: (err: unknown) => void;
  onClear: () => void;
  onSuccess: () => void;
  onEditYear: (year: number) => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertPayrollTargets();
  const yearNum = Number(year);

  const targetsFor = (y: number): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const row of teamTargets) {
      if (row.year === y) map[row.teamId] = String(toPoints(row.targetPct));
    }
    return map;
  };
  const storedRevenue = (y: number): number | null =>
    yearConfigs.find((row) => row.year === y)?.revenuePct ?? null;
  const revenueFor = (y: number): string => {
    const r = storedRevenue(y);
    return r == null ? '15' : String(toPoints(r));
  };
  const revenueIsProposed = storedRevenue(yearNum) == null;

  const [revenue, setRevenue] = useState(() => revenueFor(yearNum));
  const [teamPts, setTeamPts] = useState<Record<string, string>>(() => targetsFor(yearNum));

  const sumPts = useMemo(() => sumTeamTargetPoints(teamPts), [teamPts]);
  const remaining = useMemo(() => remainingTeamTargetPoints(teamPts), [teamPts]);
  const isOver = useMemo(() => isTeamTargetsOver100(teamPts), [teamPts]);

  const tableRows: TargetsTableRow[] = useMemo(() => {
    const years = new Set<number>();
    for (const y of yearConfigs) if (y.revenuePct != null) years.add(y.year);
    for (const row of teamTargets) years.add(row.year);
    return [...years]
      .sort((a, b) => b - a)
      .map((y) => {
        const targets = teamTargets.filter((row) => row.year === y);
        return {
          year: y,
          revenuePct: yearConfigs.find((c) => c.year === y)?.revenuePct ?? null,
          teamCount: targets.length,
          sum: targets.reduce((s, row) => s + row.targetPct, 0),
        };
      });
  }, [yearConfigs, teamTargets]);

  const columns: Column<TargetsTableRow>[] = [
    { key: 'year', header: t('gest:admin.remuneration.config.targets.year'), render: (r) => String(r.year) },
    {
      key: 'revenue',
      header: t('gest:admin.remuneration.config.targets.revenueShort'),
      render: (r) => (r.revenuePct == null ? '—' : `${toPoints(r.revenuePct)} %`),
    },
    {
      key: 'teams',
      header: t('gest:admin.remuneration.config.targets.teamsTargeted'),
      render: (r) => (r.teamCount === 0 ? '—' : String(r.teamCount)),
    },
    {
      key: 'sum',
      header: t('gest:admin.remuneration.config.targets.teamsSum'),
      render: (r) => (r.teamCount === 0 ? '—' : `${toPoints(r.sum)} %`),
    },
    {
      key: 'edit',
      header: t('gest:admin.remuneration.config.editYear'),
      render: (r) => (
        <button
          type="button"
          className={`${GS_COMPONENTS.btnGhost} touch-manipulation`}
          onClick={() => onEditYear(r.year)}
        >
          {t('gest:admin.remuneration.config.editYear')}
        </button>
      ),
    },
  ];

  function submit(e: FormEvent) {
    e.preventDefault();
    if (isOver) return;
    const y = Number(year);
    const rev = Number(revenue);
    if (!Number.isInteger(y) || y < 2000 || y > 2100 || !Number.isFinite(rev) || rev < 0 || rev > 100) return;
    const targets: { teamId: string; targetPct: number }[] = [];
    for (const [teamId, raw] of Object.entries(teamPts)) {
      if (raw.trim() === '') continue;
      const pts = Number(raw);
      if (!Number.isFinite(pts) || pts < 0 || pts > 100) return;
      targets.push({ teamId, targetPct: toFraction(pts) });
    }
    onClear();
    upsert.mutate({ year: y, revenuePct: toFraction(rev), targets }, { onError, onSuccess });
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={t('gest:admin.remuneration.config.targets.title')}
        description={t('gest:admin.remuneration.config.targets.description')}
      />
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="ad2-targets-revenue">{t('gest:admin.remuneration.config.targets.revenue')}</FieldLabel>
            <input
              id="ad2-targets-revenue"
              ref={inputRef}
              autoFocus={autoFocus}
              type="number"
              min={0}
              max={100}
              step="0.1"
              required
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              className={INPUT_MEDIUM}
            />
            {revenueIsProposed ? (
              <p className="text-chip text-slate-500 dark:text-slate-400">
                {t('gest:admin.remuneration.config.targets.proposedHint')}
              </p>
            ) : null}
          </div>
        </div>

        {teams.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:admin.remuneration.config.targets.perTeam')}
            </span>
            <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {teams.map((team) => (
                <div key={team.id} className="flex items-center justify-between gap-3">
                  <label
                    htmlFor={`ad2-target-${team.id}`}
                    className="truncate text-meta text-slate-500 dark:text-slate-400"
                  >
                    {team.name}
                  </label>
                  <input
                    id={`ad2-target-${team.id}`}
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={teamPts[team.id] ?? ''}
                    onChange={(e) => setTeamPts((prev) => ({ ...prev, [team.id]: e.target.value }))}
                    className={INPUT_SHORT}
                  />
                </div>
              ))}
            </div>
            <p
              className={`text-chip ${isOver ? 'font-medium text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}
            >
              {t('gest:admin.remuneration.config.targets.sumLine', { n: roundPoints(sumPts) })}
              {' · '}
              {t('gest:admin.remuneration.config.targets.remaining', { n: remaining })}
            </p>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={upsert.isPending || isOver}
            className={GS_COMPONENTS.btnAction}
          >
            {t('gest:admin.remuneration.config.targets.save')}
          </button>
          {isOver ? (
            <span className="text-chip text-red-600 dark:text-red-400">
              {t('gest:admin.remuneration.config.targets.overLimit')}
            </span>
          ) : null}
        </div>
      </form>

      {tableRows.length === 0 ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:admin.remuneration.config.targets.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <DataTable columns={columns} data={tableRows} rowKey={(r) => String(r.year)} />
        </div>
      )}
    </div>
  );
}
